import React, { useReducer, useState } from "react";
import {
	Box,
	TextField,
	Button,
	MenuItem,
	FormControl,
	InputLabel,
	Select,
	Typography,
	Grid,
	FormControlLabel,
	Checkbox,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { registerUser } from "../../../services/operations/userOperations";
import Loader from "../../common/Loader";
import CustomAlert from "../../common/CustomAlert";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

const initialState = {
	profileFor: "",
	firstName: "",
	lastName: "",
	gender: "",
	mobile: "",
	countryCode: "+91",
	phoneNumber: "",
	annualIncome: 0,
	loading: false,
	error: null,
	alertOpen: false,
	alertMessage: "",
	alertSeverity: "success",
	errors: {
		firstName: "",
		lastName: "",
		phoneNumber: "",
	},
};

const formReducer = (state, action) => {
	switch (action.type) {
		case "SET_FIELD":
			return {
				...state,
				[action.field]: action.value,
			};
		case "SET_LOADING":
			return {
				...state,
				loading: action.loading,
			};
		case "SET_ERROR":
			return {
				...state,
				errors: {
					...state.errors,
					[action.field]: action.error,
				},
			};
		case "RESET_FORM":
			return initialState;
		case "SET_ALERT":
			return {
				...state,
				alertOpen: action.open,
				alertMessage: action.message,
				alertSeverity: action.severity,
			};
		default:
			return state;
	}
};

const RegistrationForm = ({ onRegister }) => {
	const [state, dispatch] = useReducer(formReducer, initialState);
	const [termsOpen, setTermsOpen] = useState(false);
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [termsAcceptedInPopup, setTermsAcceptedInPopup] = useState(true);
	const [termsError, setTermsError] = useState(false);
	const [incomeErrorOpen, setIncomeErrorOpen] = useState(false);

	const [formData, setFormData] = useState({
		profileFor: "Self",
		firstName: "",
		lastName: "",
		mobile: "",
		gender: "",
		countryCode: "+91",
		email: ""
	});

	const validateField = (name, value) => {
		let error = "";
		switch (name) {
			case "firstName":
			case "lastName":
				if (!value.trim()) {
					error = `${name === "firstName" ? "First" : "Last"} name is required`;
				} else if (!/^[A-Za-z\s]+$/.test(value)) {
					error = "Only letters are allowed";
				}
				break;
			case "phoneNumber":
				if (!value) {
					error = "Mobile number is required";
				} else if (!/^[0-9]{10}$/.test(value)) {
					error = "Enter valid 10-digit mobile number";
				}
				break;
			case "email":
				if (!value.trim()) {
					error = "Email is required";
				} else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value)) {
					error = "Enter a valid email address";
				}
				break;
			default:
				break;
		}

		dispatch({ type: "SET_ERROR", field: name, error });
		return !error;
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		dispatch({ type: "SET_FIELD", field: name, value });
		validateField(name, value);
	};

	const handlePhoneChange = (value, country) => {
		dispatch({
			type: "SET_FIELD",
			field: "phoneNumber",
			value: value.slice(country.dialCode.length),
		});
		dispatch({
			type: "SET_FIELD",
			field: "countryCode",
			value: country.dialCode,
		});
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		// if ((!state.annualIncome || parseInt(state.annualIncome, 10) < 240000) && state?.gender == "male") {
		// 	setIncomeErrorOpen(true);
		// 	return;
		//   }

		if (acceptedTerms) {

			// Ensure countryCode starts with '+'
			const formattedCountryCode = state.countryCode?.startsWith("+")
				? state.countryCode
				: `+${state.countryCode}`;

			// Validate all fields
			const isFirstNameValid = validateField("firstName", state.firstName);
			const isLastNameValid = validateField("lastName", state.lastName);
			const isPhoneValid = validateField("phoneNumber", state.phoneNumber);
			const isGender = validateField('gender', state.gender)

			if (
				!isFirstNameValid ||
				!isLastNameValid ||
				!isPhoneValid ||
				!isGender ||
				!state.profileFor || !state.email
			) {
				dispatch({
					type: "SET_ALERT",
					open: true,
					message: "Please fill all fields correctly",
					severity: "error",
				});
				return;
			}

			dispatch({ type: "SET_LOADING", loading: true });

			try {
				const formData = {
					profileFor: state.profileFor,
					firstName: state.firstName,
					lastName: state.lastName,
					mobile: state.phoneNumber,
					gender: state.gender,
					countryCode: formattedCountryCode,
					email: state.email
				};
				// if (state.gender == "male") {
				// 	formData.anualIncome = state?.annualIncome
				// }
				const response = await registerUser(formData);

				if (!response.status) {
					dispatch({
						type: "SET_ALERT",
						open: true,
						message: response.message,
						severity: "error",
					});
					return;
				}

				dispatch({
					type: "SET_ALERT",
					open: true,
					message: "OTP sent successfully!",
					severity: "success",
				});

				setTimeout(() => {
					onRegister(state.phoneNumber);
					dispatch({ type: "RESET_FORM" });
				}, 1000);
			} catch (err) {
				dispatch({
					type: "SET_ALERT",
					open: true,
					message: err.message || "Registration failed",
					severity: "error",
				});
			} finally {
				dispatch({ type: "SET_LOADING", loading: false });
			}
		} else {
			setTermsError(true)
		}
	};

	const handleAlertClose = () => {
		dispatch({
			type: "SET_ALERT",
			open: false,
			message: "",
			severity: "success",
		});
	};

	return (
		<Box
			sx={{
				bgcolor: "none",
				width: { xs: "100%", sm: "346px" },
				height: { xs: "auto", sm: "409px" },
				borderRadius: "12px",
				top: "277px",
				left: "1014px",
				margin: { xs: "auto", sm: "0" },
				display: { xs: "flex", sm: "block" },
				justifyContent: { xs: "center", sm: "initial" },
				alignItems: { xs: "center", sm: "initial" },
			}}
		>
			<CustomAlert
				open={state.alertOpen}
				message={state.alertMessage}
				severity={state.alertSeverity}
				onClose={handleAlertClose}
			/>
			{state.loading ? (
				<Loader />
			) : (
				<Box className="form-card">
					<Typography
						variant="h5"
						component="h2"
						align="center"
						gutterBottom
						sx={{
							fontFamily: '"Inter var"', 
							fontWeight: 600,
							fontSize: { xs: "16px", sm: "20px" },
							lineHeight: "100%",
							letterSpacing: "0%",
							marginBottom: "20px",
						}}
					>
						Find Your Perfect Match
					</Typography>
					<form onSubmit={handleSubmit}>
						<FormControl
							fullWidth
							margin="dense"
							sx={{
								mb: 1,
								"& .MuiInputLabel-root": {
									backgroundColor: "white",
									padding: "0 8px",
									transform: "translate(14px, -8px) scale(0.75)",
									"&.Mui-focused": {
										transform: "translate(14px, -8px) scale(0.75)",
									},
									"&.MuiFormLabel-filled": {
										transform: "translate(14px, -8px) scale(0.75)",
									},
								},
								"& .MuiOutlinedInput-root": {
									"& fieldset": {
										top: 0,
									},
									"& .MuiOutlinedInput-notchedOutline": {
										top: 0,
									},
									width: "100%",
								},
							}}
						>
							<InputLabel id="profile-for-label">
								Matrimony Profile for
							</InputLabel>
							<Select
								name="profileFor"
								value={state.profileFor}
								onChange={handleChange}
								label="Matrimony Profile for"
								required
								size="small"
							>
								<MenuItem value="Self">Self</MenuItem>
								<MenuItem value="Son">Son</MenuItem>
								<MenuItem value="Daughter">Daughter</MenuItem>
								<MenuItem value="Brother">Brother</MenuItem>
								<MenuItem value="Sister">Sister</MenuItem>
								<MenuItem value="Relative">Relative</MenuItem>
								<MenuItem value="Friend">Friend</MenuItem>
							</Select>
						</FormControl>

						<TextField
							fullWidth
							margin="dense"
							label="Name"
							name="firstName"
							value={state.firstName}
							onChange={handleChange}
							required
							size="small"
							sx={{ width: "100%" }}
							error={!!state.errors.firstName}
							helperText={state.errors.firstName}
						/>
						<TextField
							fullWidth
							margin="dense"
							label="Surname"
							name="lastName"
							value={state.lastName}
							onChange={handleChange}
							required
							size="small"
							sx={{ width: "100%" }}
							error={!!state.errors.lastName}
							helperText={state.errors.lastName}
						/>
						<TextField
							select
							fullWidth
							margin="dense"
							label="Gender"
							name="gender"
							value={state.gender}
							onChange={handleChange}
							required
							size="small"
							sx={{ width: "100%" }}
							error={!!state.errors.gender}
							helperText={state.errors.gender}
						>
							<MenuItem value="male">Male</MenuItem>
							<MenuItem value="female">Female</MenuItem>
						</TextField>

						{/* {state.gender == "male" ? <TextField
  fullWidth
  margin="dense"
  label="Annual Income"
  name="annualIncome"
  type="number"
  value={state.annualIncome}
  onChange={handleChange}
  required
  size="small"
  sx={{ width: "100%" }}
  error={!!state.errors.annualIncome}
  helperText={state.errors.annualIncome}
/>
 : null} */}

						<Grid container spacing={2} sx={{ mt: 1 }}>
							<Grid item xs={12}>
								<PhoneInput
									country={"in"}
									value={state.countryCode + state.phoneNumber}
									onChange={handlePhoneChange}
									containerStyle={{
										width: "100%",
									}}
									inputStyle={{
										width: "100%",
										height: "45px",
										fontSize: "14px",
										paddingLeft: "48px",
										borderColor: state.errors.phoneNumber
											? "#d32f2f"
											: "#e0e0e0",
									}}
									buttonStyle={{
										backgroundColor: "transparent",
										borderColor: state.errors.phoneNumber
											? "#d32f2f"
											: "#e0e0e0",
										borderRadius: "4px 0 0 4px",
									}}
								/>
								{state.errors.phoneNumber && (
									<Typography variant="caption" color="error">
										{state.errors.phoneNumber}
									</Typography>
								)}
							</Grid>
						</Grid>
						<TextField
							fullWidth
							margin="dense"
							label="Email"
							name="email"
							value={state.email}
							onChange={handleChange}
							required
							size="small"
							sx={{ width: "100%" }}
							error={!!state.errors.email}
							helperText={state.errors.email}
						/>

						{state.error && (
							<Typography color="error" variant="body2" sx={{ mt: 1 }}>
								{state.error}
							</Typography>
						)}
						<FormControlLabel
							control={
								<Checkbox
									checked={acceptedTerms}
									onChange={(e) => {
										setAcceptedTerms(e.target.checked);
										if (e.target.checked) setTermsOpen(true)
									}}
									name="terms"
								/>
							}
							label={
								<Box>
									<Typography
										sx={{ cursor: "pointer", textDecoration: "underline",  
											fontSize:"15px" ,     fontFamily: '"Inter var"', 
										}}
										onClick={() => setTermsOpen(true)}
									>
										I agree to the Terms and Conditions
									</Typography>
									{!acceptedTerms && termsError && (
										<Typography variant="caption" color="error">
											Terms and Conditions are required
										</Typography>
									)}
								</Box>
							}
						/>


						<Button
							type="submit"
							fullWidth
							disabled={state.loading}
							endIcon={<ArrowForwardIcon />}
							sx={{
								mt: 2,
								py: 1,
							 fontFamily: '"Inter var"',
								backgroundColor: "#C1721F",
								color: "white",
								fontWeight: 700,
								fontSize: { xs: "16px", sm: "18px" },
								lineHeight: "100%",
								width: { xs: "100%", sm: "350px" },
								letterSpacing: "0%",
								"&:hover":{
									backgroundColor:"#C1721F",
									color:"#fff"
								}
							}}
						>
							{state.loading ? "Registering..." : "REGISTER FREE"}
						</Button>
					</form>
				</Box>
			)}
			<Dialog open={termsOpen} onClose={() => setTermsOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle sx={{ fontWeight: 700, fontSize: "20px", backgroundColor: "#C1721F", color: "white" , fontFamily: '"Inter var"'}}>
					Matrimony Terms & Conditions
				</DialogTitle>
				<DialogContent dividers>
					<Typography variant="h6" gutterBottom sx={{ fontFamily: '"Inter var"'}}>
						Please read and accept the terms to continue:
					</Typography>
					<ul style={{ paddingLeft: 20,fontFamily: '"Inter var"' }}>
						<li>Your annual income must be more than ₹2,40,000.</li>
						<li>Profile information must be truthful and verifiable.</li>
						<li>Only individuals with serious intentions may proceed.</li>
						<li>No offensive or abusive behavior tolerated.</li>
						<li>Respect all members and their privacy.</li>
					</ul>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => { setTermsOpen(false); setAcceptedTerms(false); }} sx={{ color: "#C1721F", fontWeight: 600 ,fontFamily: '"Inter var"'}}>
						Cancel
					</Button>
					<Button
						variant="contained"
						sx={{ backgroundColor: "#C1721F", color: "white", fontWeight: 600,fontFamily: '"Inter var"' }}
						onClick={() => {
							setTermsAcceptedInPopup(true);
							setTermsOpen(false);
							setAcceptedTerms(true);
						}}
					>
						Accept
					</Button>

				</DialogActions>
			</Dialog>
			<Dialog open={incomeErrorOpen} onClose={() => setIncomeErrorOpen(false)}>
				<DialogTitle sx={{ fontWeight: 600 }}>Income Eligibility</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body1" gutterBottom>
						⚠️ <strong>English:</strong> As per our eligibility criteria, users with an annual income below ₹2,40,000 are not eligible to register for matrimony services. We strive to create meaningful matches based on financial and social compatibility.
					</Typography>
					<Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
						⚠️ <strong>తెలుగు:</strong> మా అర్హత ప్రమాణాల ప్రకారం, సంవత్సర ఆదాయం ₹2,40,000 కన్నా తక్కువ ఉన్నవారు ఈ మ్యాట్రిమోనీ సేవలకు నమోదు చేసుకునే అర్హత పొందరు. ఇది ఆర్థిక మరియు సామాజిక సాంపత్యం ఆధారంగా మంచి సంబంధాలు ఏర్పడేలా చేయడమే లక్ష్యం.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIncomeErrorOpen(false)} autoFocus>
						OK
					</Button>
				</DialogActions>
			</Dialog>


		</Box>

	);
};

export default RegistrationForm;
